import { use, useId, useMemo, useState } from 'react';
import {
  COMPONENTS,
  findComponent,
  type ComponentSchema,
  type ComponentTemplate,
  type Scene,
  withTemplateKnobs,
  withTemplateScenes,
} from './schema/components';
import { LAB_SECTIONS } from './schema/labComponents';
import { ComponentSection } from './ComponentSection';
import { LabActiveContext } from './LabActiveContext';

export interface SectionEditorProps {
  /** When set, hide components that belong to the other template. */
  template?: ComponentTemplate;
  /**
   * Schema `name` of the component the operator just clicked in the preview
   * iframe (see `useSelectedComponent`). When non-null the matching scene
   * group is force-opened, the corresponding `ComponentSection` is told to
   * expand + scroll into view, and the selection-highlight is drawn around
   * it. Manual `<details>` toggles by the operator still work — this is
   * additive, not authoritative.
   */
  selectedComponent?: string | null;
  /** DS variant the operator clicked — forwarded to ComponentSection so
   *  its variant picker auto-switches. See `useChildListener.useClickToEdit`. */
  selectedVariant?: string;
  /** Bumped on every click; lets ComponentSection re-fire scroll/highlight
   *  on a repeat click of the same component. */
  selectionNonce?: number;
  /** When set, the editor only shows components whose `scenes` includes
   *  this value (plus cross-cutting ones with no `scenes` field). Driven
   *  by `useIframeScene` in the parent — keeps the sidebar in sync with
   *  the route the preview iframe is on. `undefined` = show every scene. */
  currentScene?: Scene;
}

interface SceneGroupMeta {
  scene: Scene;
  title: string;
  /** Unicode glyph rendered before the title — kept minimal, no emojis. */
  icon: string;
  defaultOpen: boolean;
  /** Sub-headings the group lists its components under, in this order, by
   *  each component's `section`. Unset = one flat list. */
  sections?: readonly string[];
  /** The last sub-heading: whatever has no `section` of the list above (the
   *  cross-cutting page parts, the dialogs and toasts). */
  otherSection?: string;
}

const SCENE_GROUPS: readonly SceneGroupMeta[] = [
  { scene: 'pack', title: 'Pack page', icon: '□', defaultOpen: true },
  { scene: 'just-pulled', title: 'Just Pulled page', icon: '▥', defaultOpen: true },
  { scene: 'my-packs', title: 'My Packs page', icon: '▤', defaultOpen: true },
  { scene: 'reveal', title: 'Reveal (after opening pack)', icon: '◇', defaultOpen: false },
];

/** The lab's group joins the list only while the lab is in play, and starts closed like the reveal's. */
const LAB_GROUP: SceneGroupMeta = {
  scene: 'custom-pack',
  title: 'Custom Pack Lab',
  icon: '▦',
  defaultOpen: false,
  sections: LAB_SECTIONS,
  otherSection: 'Page, dialogs & toasts',
};

const SCENE_GROUPS_WITH_LAB: readonly SceneGroupMeta[] = [...SCENE_GROUPS, LAB_GROUP];

/** Not listed on `scene`: a cross-cutting component nothing there renders. */
function excludedFrom(c: ComponentSchema, scene: Scene): boolean {
  return c.excludeScenes?.includes(scene) ?? false;
}

/** Only the lab renders it, so without the lab's group it has nowhere to be listed. */
function labOnly(c: ComponentSchema): boolean {
  return c.scenes !== undefined && c.scenes.length > 0 && c.scenes.every((scene) => scene === LAB_GROUP.scene);
}

interface SectionBucket {
  title: string;
  items: ComponentSchema[];
}

/** `items` under the group's sub-headings, in their order; an empty one is left out. */
function sectionsOf(meta: SceneGroupMeta, items: readonly ComponentSchema[]): SectionBucket[] | null {
  if (!meta.sections) return null;
  const known = new Set(meta.sections);
  const buckets: SectionBucket[] = meta.sections.map((title) => ({
    title,
    items: items.filter((c) => c.section === title),
  }));
  const rest = items.filter((c) => c.section === undefined || !known.has(c.section));
  buckets.push({ title: meta.otherSection ?? 'Other', items: rest });
  return buckets.filter((bucket) => bucket.items.length > 0);
}

/**
 * SectionEditor — palette-first sidebar replacement for the old flat list.
 * Groups COMPONENTS by `schema.scenes`:
 *   - A component with `scenes: ['pack', 'reveal']` is listed under EACH of
 *     its declared scenes, so it's reachable wherever it appears. Only the
 *     active scene's group renders at a time, so the operator never sees
 *     duplicates in normal use.
 *   - A component with no `scenes` field is "cross-cutting" and rendered
 *     under every scene group, so partners can find it wherever they expect,
 *     but for the scenes it names in `excludeScenes`.
 *   - A group with `sections` (the Custom Pack Lab) lists its components
 *     under sub-headings, by each component's `section`. That group exists
 *     only while the lab is in play (`LabActiveContext`, or the preview on
 *     the lab); otherwise the four page groups are all there is.
 *
 * Search still filters, but matches against `displayName ?? name` (human
 * label) and `description` rather than the technical identifier. aria-labels on each section
 * continue to use the technical `name` (stable for tests).
 */
export function SectionEditor({
  template,
  selectedComponent = null,
  selectedVariant,
  selectionNonce,
  currentScene,
}: SectionEditorProps = {}) {
  const [query, setQuery] = useState('');
  const labInPlay = use(LabActiveContext);
  // The lab's group: the page previews the lab, or the preview has reported it is on it.
  const listsLab = labInPlay || currentScene === LAB_GROUP.scene;
  const groups = listsLab ? SCENE_GROUPS_WITH_LAB : SCENE_GROUPS;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const inTemplate = (c: ComponentSchema) =>
      template === undefined || c.templates === undefined || c.templates.includes(template);
    // Scene filter — show only components actually present on the current
    // preview route. `currentScene === undefined` (e.g. before the iframe
    // reports its path) falls through to "show all" so the operator
    // isn't locked out during the initial-load grace window — all but the
    // lab's parts, unless the lab's group is listed.
    const inScene = (c: ComponentSchema) => {
      if (!currentScene) return listsLab || !labOnly(c);
      // No `scenes` declared = cross-cutting (e.g. global PageBackground),
      // unless the scene renders none of it (`excludeScenes`).
      if (!c.scenes || c.scenes.length === 0) return !excludedFrom(c, currentScene);
      return c.scenes.includes(currentScene);
    };
    const base = COMPONENTS.filter(inTemplate)
      .map((c) => withTemplateKnobs(withTemplateScenes(c, template), template))
      .filter(inScene);
    if (!q) return base;
    // The label, or where the component is on the page ("trash" finds the
    // odds row).
    return base.filter((c) => {
      const label = (c.displayName ?? c.name).toLowerCase();
      return label.includes(q) || (c.description?.toLowerCase().includes(q) ?? false);
    });
  }, [query, template, currentScene, listsLab]);

  // Build the grouped index. A component without `scenes` is shared across
  // every group so partners always find it. A component with `scenes` is
  // listed under each declared scene (only the active one renders at a time).
  const grouped = useMemo(() => {
    const byScene: Record<Scene, ComponentSchema[]> = {
      pack: [],
      'just-pulled': [],
      'my-packs': [],
      reveal: [],
      'custom-pack': [],
    };
    const cross: ComponentSchema[] = [];
    for (const c of filtered) {
      const scenes = c.scenes;
      if (!scenes || scenes.length === 0) {
        cross.push(c);
        continue;
      }
      // A component may declare several scenes (e.g. the ShippingModal
      // appears in both `reveal` and `my-packs`). List it under EACH so
      // it's reachable from every scene it actually appears in. In normal
      // operation only the active scene's group renders, so there are no
      // visible duplicates — they only coincide in the brief "show all"
      // grace window before the iframe first reports its route.
      for (const s of scenes) byScene[s].push(c);
    }
    return { byScene, cross };
  }, [filtered]);

  const hasAny = filtered.length > 0;

  // Resolve the selected component's primary scene. Cross-cutting (no
  // `scenes`) components don't have a single home — for those, pin
  // selection to the iframe's *current* scene so that group auto-opens
  // and the highlighted ComponentSection is actually reachable.
  // Without this fallback a cross-cutting selection (e.g. right-click
  // on `<Button>` inside the reveal overlay) left every scene group
  // collapsed because no scene matched `null`.
  const selectedScene: Scene | null = useMemo(() => {
    if (selectedComponent === null) return null;
    const found = findComponent(selectedComponent);
    if (!found) return null;
    const schema = withTemplateScenes(found, template);
    // Multi-scene component (e.g. ShippingModal in reveal + my-packs):
    // pin selection to the scene the iframe is ACTUALLY on so that
    // group force-opens on right-click — not just its first declared
    // scene (which would leave the active group collapsed if the
    // operator had manually closed it).
    if (schema.scenes && currentScene && schema.scenes.includes(currentScene)) {
      return currentScene;
    }
    const first = schema.scenes?.[0];
    if (first) return first;
    return currentScene ?? null;
  }, [selectedComponent, currentScene, template]);

  return (
    <div className="flex flex-col gap-3">
      <input
        type="search"
        placeholder="Search components…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="rounded border border-neutral-700 bg-neutral-900 px-3 py-1.5 font-mono text-[12px] text-white outline-none"
      />

      {!hasAny ? (
        <div className="rounded border border-dashed border-neutral-800 px-3 py-6 text-center font-mono text-[11px] text-neutral-500">
          No components match
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {groups.filter(
            // When the iframe reports a scene, only render that scene's
            // group. Other groups carry cross-cutting components (e.g.
            // global PageBackground) which would otherwise make every
            // group non-empty and visible at once — exactly the confusion
            // the operator just hit.
            (meta) => !currentScene || meta.scene === currentScene,
          ).map((meta) => {
            const sceneItems = grouped.byScene[meta.scene];
            const items = [...sceneItems, ...grouped.cross.filter((c) => !excludedFrom(c, meta.scene))];
            if (items.length === 0) return null;
            // When a query is active, force-open all matching groups so the
            // user can see hits without an extra click. Otherwise honour the
            // configured default-open flag for the scene. The selection
            // path also forces the matching scene open (the `key` change
            // remounts the <details> so React picks up the new `open`).
            const matchesSelected = selectedScene === meta.scene;
            const open =
              query.trim() !== '' ? true : matchesSelected || meta.defaultOpen;
            return (
              <SceneGroup
                // Key includes the selection nonce so re-clicking the same
                // section's group force-remounts the <details> with
                // `open=true` even if the operator manually collapsed it.
                key={`${meta.scene}-${matchesSelected ? `${selectedComponent}-${selectionNonce ?? 0}` : ''}`}
                meta={meta}
                items={items}
                openByDefault={open}
                selectedComponent={selectedComponent}
                {...(selectedVariant ? { selectedVariant } : {})}
                {...(selectionNonce !== undefined ? { selectionNonce } : {})}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

interface SceneGroupProps {
  meta: SceneGroupMeta;
  items: ComponentSchema[];
  openByDefault: boolean;
  selectedComponent: string | null;
  selectedVariant?: string;
  selectionNonce?: number;
}

/**
 * Native HTML `<details>` disclosure — keyboard-accessible by default and
 * preserves open state across re-renders unless `key`/`open` prop changes.
 * `openByDefault` only seeds the initial DOM state; React doesn't
 * synchronise it back during re-renders, which is what we want for a
 * scene group the user has manually toggled.
 */
function SceneGroup({
  meta,
  items,
  openByDefault,
  selectedComponent,
  selectedVariant,
  selectionNonce,
}: SceneGroupProps) {
  const idPrefix = useId();
  const sections = sectionsOf(meta, items);
  const sectionId = (index: number) => `${idPrefix}section-${index}`;

  const renderItem = (schema: ComponentSchema) => {
    const isSel = selectedComponent === schema.name;
    const section = (
      <ComponentSection
        key={schema.name}
        schema={schema}
        isSelected={isSel}
        {...(isSel && selectedVariant ? { selectedVariant } : {})}
        {...(isSel && selectionNonce !== undefined ? { selectionNonce } : {})}
      />
    );
    // Where the part is on the page, on hover.
    if (!schema.description) return section;
    return (
      <div key={schema.name} title={schema.description}>
        {section}
      </div>
    );
  };

  return (
    <details
      open={openByDefault}
      className="rounded border border-neutral-800 bg-neutral-950"
    >
      <summary
        aria-label={`Scene group ${meta.scene}`}
        className="flex cursor-pointer items-center gap-2 px-3 py-2 select-none hover:bg-neutral-900"
      >
        <span aria-hidden className="text-[13px] leading-none text-neutral-400">
          {meta.icon}
        </span>
        <span className="font-mono text-[12px] font-semibold text-white">
          {meta.title}
        </span>
        <span className="font-mono text-[10px] text-neutral-500">
          ({items.length} {items.length === 1 ? 'component' : 'components'})
        </span>
      </summary>
      <div className="flex flex-col gap-2 border-t border-neutral-800 px-2 py-2">
        {sections ? (
          <>
            {sections.length > 2 && (
              // A long group: one jump per sub-heading.
              <nav aria-label={`${meta.title} sections`} className="flex flex-wrap gap-1 px-1 pt-1">
                {sections.map((bucket, index) => (
                  <button
                    key={bucket.title}
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(sectionId(index))
                        ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
                    }
                    className="rounded-full border border-neutral-800 px-2 py-0.5 font-mono text-[10px] text-neutral-400 transition-colors hover:border-neutral-600 hover:text-white"
                  >
                    {bucket.title}
                  </button>
                ))}
              </nav>
            )}
            {sections.map((bucket, index) => (
              <section
                key={bucket.title}
                id={sectionId(index)}
                aria-labelledby={`${sectionId(index)}-title`}
                className="flex scroll-mt-1 flex-col gap-2"
              >
                {/* Sticks while its components scroll by, so the operator
                    always knows which part of the page they are in. */}
                <h3
                  id={`${sectionId(index)}-title`}
                  className="sticky top-0 z-10 -mx-2 flex items-center gap-2 bg-neutral-950 px-3 pt-2.5 pb-1 font-mono text-[10px] font-semibold tracking-[0.12em] text-neutral-300 uppercase"
                >
                  <span className="shrink-0">{bucket.title}</span>
                  <span aria-hidden className="h-px flex-1 bg-neutral-800" />
                  <span aria-hidden className="font-normal tracking-normal text-neutral-600">
                    {bucket.items.length}
                  </span>
                </h3>
                {bucket.items.map(renderItem)}
              </section>
            ))}
          </>
        ) : (
          items.map(renderItem)
        )}
      </div>
    </details>
  );
}
